// Non-visual logic for the layer drill-downs — the jobs behind one band of the
// Month Landing chart.
//
// The panel's job is to be checkable against the bar above it, so the two
// things here are about exactly that: which date explains a given layer, and
// what to say when the table's total and the bar's segment disagree. They do
// disagree, legitimately and often: the bar is the morning snapshot and these
// lines are live, so a job invoiced or approved since 02:31 is in one and not
// the other. Saying so is the difference between a panel that builds trust and
// one that quietly destroys it.
//
// See docs/features/month-landing-forecast/38_booked_layer_drilldowns.md.

import type { MonthLandingLine } from "../data/api-types";
import type { MonthLandingMetric } from "./month-landing";

/** The layers that have jobs behind them, keyed as the chart's bands are. */
export const DRILLABLE_LAYERS = [
  "invoiced",
  "billed_in_advance",
  "whites",
  "booked_approved",
  "booked_entered",
  "accrual_adjustment",
  "wip_carry",
  "stock_not_invoiced",
] as const;

export type DrillableLayer = (typeof DRILLABLE_LAYERS)[number];

export function isDrillableLayer(layer: string): layer is DrillableLayer {
  return (DRILLABLE_LAYERS as readonly string[]).includes(layer);
}

/**
 * Which of a line's three dates explains it, per layer, and what to call it.
 *
 * A booked line is explained by when it is due, a white by when it went out, an
 * invoiced line by when it was billed. All three ship on every row because the
 * answer depends on the layer; this is where the page decides.
 */
export function layerDateColumn(layer: string): {
  heading: string;
  field: "due_at" | "invoiced_at" | "first_delivered_at";
} | null {
  switch (layer) {
    case "invoiced":
    case "billed_in_advance":
      return { heading: "Invoiced", field: "invoiced_at" };
    case "whites":
      return { heading: "Despatched", field: "first_delivered_at" };
    case "booked_approved":
    case "booked_entered":
    case "wip_carry":
      return { heading: "Due", field: "due_at" };
    // stock_not_invoiced is due-date-free by design (its due date is production
    // scheduling, not revenue landing) and an accrual is a period statement.
    default:
      return null;
  }
}

/** The money on a line for the metric the page is showing. */
export function lineAmount(
  line: MonthLandingLine,
  metric: MonthLandingMetric,
): number {
  return metric === "contribution" ? line.contribution_amt : line.revenue_amt;
}

/**
 * The gap between the band on the chart and the table under it, when there is
 * one worth naming.
 *
 * Returns null below a pound, which is rounding rather than drift. Signed: the
 * bar can be ahead (work has invoiced since the snapshot and left the layer) or
 * behind (work has been booked into it since).
 */
export function snapshotDrift(
  bandTotal: number,
  linesTotal: number,
): number | null {
  const drift = bandTotal - linesTotal;
  return Math.abs(drift) < 1 ? null : drift;
}

/** Distinct jobs behind the table — the count that makes "9 rows" legible. */
export function distinctJobCount(lines: readonly MonthLandingLine[]): number {
  return new Set(lines.map((l) => l.job_no).filter((j) => j !== "")).size;
}

const CSV_HEADER = [
  "Job",
  "Customer code",
  "Customer",
  "Status",
  "Lands on (day of month)",
  "Due",
  "Invoiced",
  "Despatched",
  "Value",
  "Contribution",
] as const;

/** Quote a CSV field whenever it could otherwise break the row. */
function csvField(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * The whole population as CSV — every row, not the on-screen cap.
 *
 * All three dates ship regardless of the layer: the file outlives the panel
 * that knew which one mattered, and a column silently absent is worse than one
 * that is empty for a reason. Amounts are raw numbers so the file arithmetises.
 */
export function linesToCsv(lines: readonly MonthLandingLine[]): string {
  const rows = [CSV_HEADER.join(",")];
  for (const line of lines) {
    rows.push(
      [
        line.job_no,
        line.customer_code,
        line.customer_name,
        line.status,
        line.due_day === 0 ? "whole month" : line.due_day,
        line.due_at ?? "",
        line.invoiced_at ?? "",
        line.first_delivered_at ?? "",
        line.revenue_amt,
        line.contribution_amt,
      ]
        .map(csvField)
        .join(","),
    );
  }
  return rows.join("\n") + "\n";
}

/** `month-landing-booked_entered-2026-10-company.csv` — layer, month and scope,
 *  because three downloads otherwise overwrite each other. */
export function linesCsvFilename(
  month: string,
  layer: string,
  scope: string,
): string {
  const safeScope = scope.replace(/[^a-zA-Z0-9]+/g, "-");
  return `month-landing-${layer}-${month}-${safeScope}.csv`;
}
